import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { AnyStatus } from './types';

/**
 * Maps every status enum value to exactly one MUI color token.
 * Covers HealthStatus, TemplateVersionStatus, and FormInstanceStatus.
 */
const STATUS_COLOR_MAP: Record<AnyStatus, string> = {
  // Green — success.main
  HEALTHY: 'success.main',
  PUBLISHED: 'success.main',
  COMPLETED: 'success.main',

  // Amber — warning.main
  WARNING: 'warning.main',
  DRAFT: 'warning.main',
  IN_PROGRESS: 'warning.main',

  // Red — error.main
  CRITICAL: 'error.main',
  OVERDUE: 'error.main',

  // Grey — text.secondary
  UNKNOWN: 'text.secondary',
  ARCHIVED: 'text.secondary',
  CANCELLED: 'text.secondary',
  PENDING: 'text.secondary',
};

/**
 * Returns a human-readable label for a status value.
 * Converts SCREAMING_SNAKE_CASE to Title Case.
 */
function formatStatusLabel(status: AnyStatus): string {
  return status
    .split('_')
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');
}

export interface StatusBadgeProps {
  /** The status value to display. Must be a member of HealthStatus, TemplateVersionStatus, or FormInstanceStatus. */
  status: AnyStatus;
  /** Size variant: 'small' renders dot only, 'medium' renders dot + label. Defaults to 'medium'. */
  size?: 'small' | 'medium';
  /** Whether to show the text label alongside the dot. Defaults to true for medium, false for small. */
  showLabel?: boolean;
}

/**
 * StatusBadge renders a color-coded status indicator used across all entity views.
 *
 * - Small size: colored dot only (8px)
 * - Medium size: colored dot (10px) + text label
 *
 * Color mapping:
 * - Green (success.main): HEALTHY, PUBLISHED, COMPLETED
 * - Amber (warning.main): WARNING, DRAFT, IN_PROGRESS
 * - Red (error.main): CRITICAL, OVERDUE
 * - Grey (text.secondary): UNKNOWN, ARCHIVED, CANCELLED, PENDING
 */
export const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  size = 'medium',
  showLabel,
}) => {
  const color = STATUS_COLOR_MAP[status];
  const displayLabel = showLabel ?? (size === 'medium');
  const dotSize = size === 'small' ? 8 : 10;

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: displayLabel ? 1 : 0,
      }}
      role="status"
      aria-label={`Status: ${formatStatusLabel(status)}`}
    >
      <Box
        sx={{
          width: dotSize,
          height: dotSize,
          borderRadius: '50%',
          backgroundColor: color,
          flexShrink: 0,
        }}
      />
      {displayLabel && (
        <Typography
          variant="body2"
          sx={{ color, fontWeight: 500, lineHeight: 1 }}
        >
          {formatStatusLabel(status)}
        </Typography>
      )}
    </Box>
  );
};

export default StatusBadge;
